import { X } from "lucide-react"

const InterfaceGuideModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');
      `}</style>

      <div
        className="bg-white w-full mx-4 border border-zinc-200"
        style={{
          maxWidth: "640px",
          borderRadius: 0,
          boxShadow: "4px 4px 0 #1c1c1c",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-200">
          <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-900">
            Interface Guide
          </h2>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center border border-zinc-300 hover:border-zinc-900 text-zinc-500 hover:text-zinc-900 transition-all"
            style={{ borderRadius: 0 }}
          >
            <X size={13} />
          </button>
        </div>

        {/* Editor preview */}
        <div className="px-5 pt-5">
          <div
            className="border border-zinc-200 overflow-hidden"
            style={{ borderRadius: 0 }}
          >
            {/* Mock top bar */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-200 bg-white">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                  Problem
                </span>
                <span
                  className="text-[10px] font-bold text-zinc-900"
                  style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                >
                  1 <span className="text-zinc-300">/</span> 1
                </span>
                <span
                  className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 border border-amber-300 bg-amber-50 text-amber-700 ml-1"
                  style={{
                    borderRadius: 0,
                    fontFamily: "'IBM Plex Mono', monospace",
                  }}
                >
                  ⏱ 14:45
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                  Solution
                </span>
                <span
                  className="text-[10px] font-bold px-2 py-0.5 border border-zinc-900 bg-zinc-900 text-white"
                  style={{ borderRadius: 0 }}
                >
                  Text ▾
                </span>
              </div>
            </div>

            {/* Two panel body */}
            <div className="grid grid-cols-2" style={{ height: "180px" }}>
              {/* Left — question */}
              <div className="relative border-r border-zinc-200 bg-white overflow-hidden">
                <div className="px-4 pt-4 pb-10">
                  <p
                    className="text-zinc-700 leading-relaxed"
                    style={{ fontSize: "11px" }}
                  >
                    Given an array of integers{" "}
                    <code
                      className="bg-zinc-100 border border-zinc-200 px-1 text-zinc-800"
                      style={{
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: "10px",
                      }}
                    >
                      nums
                    </code>{" "}
                    and an integer{" "}
                    <code
                      className="bg-zinc-100 border border-zinc-200 px-1 text-zinc-800"
                      style={{
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: "10px",
                      }}
                    >
                      target
                    </code>
                    , return <strong>indices</strong> of the two numbers that
                    add up to target...
                  </p>
                </div>
                {/* Label */}
                <div className="absolute bottom-0 left-0 right-0 flex items-center gap-2 px-3 py-1.5 bg-blue-50 border-t border-blue-200">
                  <div
                    className="w-4 h-4 flex items-center justify-center bg-blue-600 text-white text-[9px] font-bold shrink-0"
                    style={{ borderRadius: 0 }}
                  >
                    1
                  </div>
                  <span className="text-[10px] font-semibold text-blue-700">
                    Read question here
                  </span>
                </div>
              </div>

              {/* Right — editor */}
              <div className="relative bg-zinc-50 overflow-hidden">
                <div className="px-3 pt-3 pb-10">
                  <div
                    className="bg-white border border-zinc-200 p-2.5"
                    style={{ borderRadius: 0 }}
                  >
                    <div
                      style={{
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: "11px",
                        lineHeight: "1.8",
                        color: "#94a3b8",
                      }}
                    >
                      <div>
                        <span className="text-zinc-300 mr-2 select-none">
                          1
                        </span>
                        <span className="text-violet-500">def</span>
                        <span className="text-zinc-600">
                          {" "}
                          twoSum(nums, target):
                        </span>
                      </div>
                      <div>
                        <span className="text-zinc-300 mr-2 select-none">
                          2
                        </span>
                        <span className="text-zinc-400 ml-4">
                          # write your solution...
                        </span>
                      </div>
                      <div>
                        <span className="text-zinc-300 mr-2 select-none">
                          3
                        </span>
                        <span className="text-zinc-400 ml-4">pass</span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Label */}
                <div className="absolute bottom-0 left-0 right-0 flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border-t border-emerald-200">
                  <div
                    className="w-4 h-4 flex items-center justify-center bg-emerald-600 text-white text-[9px] font-bold shrink-0"
                    style={{ borderRadius: 0 }}
                  >
                    2
                  </div>
                  <span className="text-[10px] font-semibold text-emerald-700">
                    Write your solution here
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tip */}
        <div className="px-5 pt-4">
          <div
            className="flex items-start gap-2.5 px-3 py-2.5 bg-amber-50 border border-amber-200"
            style={{ borderRadius: 0 }}
          >
            <div
              className="w-4 h-4 flex items-center justify-center bg-amber-500 text-white text-[9px] font-bold shrink-0 mt-0.5"
              style={{ borderRadius: 0 }}
            >
              3
            </div>
            <p className="text-[11px] text-amber-800 font-medium leading-relaxed">
              The timer in the top bar tracks your time — keep an eye on it and
              stay within your limit.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-5">
          <button
            onClick={onClose}
            className="w-full bg-zinc-900 hover:bg-zinc-800 text-white font-bold text-xs uppercase tracking-widest py-3 transition-colors"
            style={{ borderRadius: 0 }}
          >
            Got It — Start Interview
          </button>
        </div>
      </div>
    </div>
  )
}

export default InterfaceGuideModal
